// src/app/models/table.model.ts
export type ColumnType = 'number' | 'boolean' | 'date' | 'string';

export interface ColumnDefinition {
  name: string;
  key: string;
  type: ColumnType;
}

export interface TableData {
  title: string;
  rows: Record<string, unknown>[];
  totalCount: number;
  columns: ColumnDefinition[];
}

export interface NotificationInfo {
  title: string;
  body: string;
  type: string;
  created: string;
}

export interface ApiResponse {
  success: boolean;
  title?: string;
  body?: string;
  type?: string;
  created?: string;
  data: string | Record<string, unknown>[];
  message?: string;
}

function safeParseArray(json: string): Record<string, unknown>[] | null {
  if (!json || typeof json !== 'string') return null;
  
  const trimmed = json.trim();
  if (!trimmed.startsWith('[') || !trimmed.endsWith(']')) return null;
  
  const isValidJson = (str: string): boolean => {
    const jsonPattern = /^[\[\{][\s\S]*[\]\}]$/;
    if (!jsonPattern.test(str)) return false;
    
    let inString = false;
    let escaped = false;
    for (let i = 0; i < str.length; i++) {
      const char = str[i];
      if (escaped) {
        escaped = false;
        continue;
      }
      if (char === '\\') {
        escaped = true;
        continue;
      }
      if (char === '"') {
        inString = !inString;
      }
    }
    return !inString;
  };
  
  if (!isValidJson(trimmed)) return null;
  
  const parsed = JSON.parse(trimmed);
  return Array.isArray(parsed) ? parsed : null;
}

export class TableModel implements TableData {
  title: string;
  rows: Record<string, unknown>[];
  totalCount: number;
  columns: ColumnDefinition[];

  constructor(init?: Partial<TableData>) {
    this.title = init?.title ?? '';
    this.rows = init?.rows ?? [];
    this.totalCount = init?.totalCount ?? 0;
    this.columns = init?.columns ?? [];
  }

  static fromRows(rows: Record<string, unknown>[], title = 'Data Report'): TableModel {
    if (!Array.isArray(rows) || rows.length === 0) {
      return new TableModel({ title, rows: [], totalCount: 0, columns: [] });
    }

    const first = rows[0];
    const keys = Object.keys(first);

    const columns: ColumnDefinition[] = keys.map(key => ({
      name: TableModel.formatColumnName(key),
      key,
      type: TableModel.detectColumnType(first[key])
    }));

    return new TableModel({
      title,
      rows,
      totalCount: rows.length,
      columns
    });
  }

  static fromApiResponse(resp: ApiResponse): { notification: NotificationInfo; table: TableModel } {
    const notification: NotificationInfo = {
      title: resp.title ?? '',
      body: resp.body ?? '',
      type: resp.type ?? '',
      created: resp.created ?? ''
    };

    const rows = typeof resp.data === 'string'
      ? (safeParseArray(resp.data) ?? [])
      : (Array.isArray(resp.data) ? resp.data : []);

    const table = TableModel.fromRows(rows, notification.title || 'Data Report');
    return { notification, table };
  }

  static formatColumnName(key: string): string {
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, c => c.toUpperCase())
      .trim();
  }

  static detectColumnType(value: unknown): ColumnType {
    if (typeof value === 'number') return 'number';
    if (typeof value === 'boolean') return 'boolean';
    if (value instanceof Date || TableModel.isDateString(value)) return 'date';
    return 'string';
  }

  static isDateString(value: unknown): boolean {
    if (typeof value !== 'string') return false;
    const dt = new Date(value);
    return !isNaN(dt.getTime()) && value.includes('T');
  }

  // This can be edited for more table types
  static formatCellValue(value: unknown, type: ColumnType): string {
    if (!value) return '-';
    switch (type) {
      case 'number':
        return typeof value === 'number' ? value.toLocaleString() : String(value);
      case 'date':
        return new Date(value as string | Date).toLocaleString();
      case 'boolean':
        return value ? 'GLOBAL.YES' : 'GLOBAL.NO';
      default:
        return String(value);
    }
  }
}